import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, combineLatest, concat, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';

import { MerchantRequestService } from '@shared/services/merchant-request.service';

import { environment } from '../../../../environments/environment';
import { BaseComponent } from '../../../base/base.component';
import { HistoryComponent } from '../../../shared/components/history/history.component';
import { emailValidator } from '../../../shared/custom-validators/email-validator';
import { ClientIdTypeEnum } from '../../../shared/enum/client-id.enum';
import { MasterDataEnum } from '../../../shared/enum/master-data.enum';
import { ModalButtonResponseEnum } from '../../../shared/enum/modal-button-response.enum';
import { NotificationTypeEnum } from '../../../shared/enum/notification-type.enum';
import { ResetPasswordTypeEnum } from '../../../shared/enum/reset-password-type.enum';
import { UserPageModes, UserStatusEnum } from '../../../shared/enum/user.enum';
import {
  GraphqlQueryObject,
  GraphqlQuerySortOptions,
  OrderByEnum,
  OrderDirectionEnum
} from '../../../shared/gql/common.gql';
import { AlertModalComponent } from '../../../shared/layouts';
import { ConfirmModalComponent } from '../../../shared/layouts/modals/confirm-modal/confirm-modal.component';
import {
  ChangeStatusUserRequest,
  Department,
  RoleViewResponse,
  StoreAuthority,
  StoreAuthorityScopeSnapshot,
  StoreAuthorityStoreSnapshot,
  UserDto,
  UserSearchCriteria,
  UserViewResponse
} from '../../../shared/models';
import { HistoryType } from '../../../shared/models/audit-log.model';
import { ConfirmModal } from '../../../shared/models/confirm-modal.mode';
import { NotificationEmit } from '../../../shared/models/notification-emit.model';
import { AuthGuardService } from '../../../shared/services';
import { MasterService } from '../../../shared/services/master.service';
import { RoleService } from '../../../shared/services/role.service';
import { StoreRequestService } from '../../../shared/services/store-request.service';
import {
  ActivateUserRequestAction,
  ActivateUserResetAction,
  UserByUserNoRequestAction,
  UserByUserNoResetAction,
  UserCreateResponseResetAction,
  UserListRequestAction,
  UserResetPasswordRequestAction,
  UserResetPasswordResponseResetAction,
  UserUpdateRequestAction,
  UserUpdateResponseResetAction
} from '../../../shared/store/actions/user-info.action';
import {
  selectResetPasswordResult,
  selectResetPinResult
} from '../../../shared/store/selectors/reset-password-pin.selector';
import {
  selectChangeStatusUsersByUsernameResult,
  selectUserCreateResponseError,
  selectUsersByNo,
  selectUsersListCriteria
} from '../../../shared/store/selectors/user-selector';
import { AppStates } from '../../../shared/store/state/app.states';
import { RoleAllRequestAction, RoleAllResetAction } from '../../roles/stores/role/role.actions';
import { selectRoleAllList } from '../../roles/stores/role/role.selectors';
import { AuthorityLevelEnum } from '../enum/store-authority.enum';
import { DepartmentOption } from '../models/department-option.model';
import {
  AuthorityLevelOption,
  AuthorityScopeOption,
  AuthorityScopeResponse,
  AuthorityScopeStore
} from '../models/store-authority.model';
import { StoreAuthorityService } from '../services/store-authority.service';
import { compareTextAsc } from '../utils/sort.util';

/**
 * Validates the string level GET /users/{no}/authority-scopes returns against the known
 * AuthorityLevelEnum values, so an unexpected string from BE maps to null instead of
 * silently casting.
 */
const VIEW_AUTHORITY_LEVEL_MAP: Record<string, AuthorityLevelEnum> = {
  ALL_STORE: AuthorityLevelEnum.ALL_STORE,
  REGIONAL_OFFICE: AuthorityLevelEnum.REGIONAL_OFFICE,
  REGIONAL_MANAGER: AuthorityLevelEnum.REGIONAL_MANAGER,
  AREA_MANAGER: AuthorityLevelEnum.AREA_MANAGER,
  STORE: AuthorityLevelEnum.STORE
};

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() notifyParent: EventEmitter<NotificationEmit> = new EventEmitter<NotificationEmit>();
  @Output() data: {
    title: string;
    mode: UserPageModes;
    userNo: string;
  };

  public userForm: UntypedFormGroup;
  public localStore: Observable<any>;
  public merchantList: Observable<any[]>;
  public merchantSearchInput$ = new Subject<string>();
  public userSelectedSubscription: Subscription;
  public merchantSearchLoading: boolean;
  public isFormDirty: boolean;
  public submitted: boolean;
  public roleList: RoleViewResponse[];
  public roleOptions: RoleViewResponse[];
  public departmentList: Array<Department>;
  public departmentOptions: DepartmentOption[];
  public departmentOptionsDisplay: DepartmentOption[];
  public hasStoreAuthority: boolean;
  public authorityLevelOptions: AuthorityLevelOption[];
  public authorityScopeResponse: AuthorityScopeResponse;
  public authorityScopeError = false;
  public authorityScopeOptions: AuthorityScopeOption[];
  public storePanelScope: AuthorityScopeOption;
  public storePanelStores: AuthorityScopeStore[];
  public isSendToSubmit: boolean;
  public criteriaObject: UserSearchCriteria;
  public duplicatePhoneNo: string;
  public userView: UserViewResponse;
  public status: string;
  public hasViewPermission = false;
  public hasManagePermission = false;
  public roleStoreOwner = ['STORE_OWNER', 'STORE_OWNER_PARTNER'];
  public isStoreOwner = false;
  public dateFormat = environment.dateFormat;
  public accessStoreAuthority: Array<ClientIdTypeEnum>;
  public viewAuthorityLevel: AuthorityLevelEnum;
  public viewScopeDetails: StoreAuthorityScopeSnapshot[];
  public viewStoresForStoreLevel: StoreAuthorityStoreSnapshot[];
  public viewAuthorityError = false;
  public readonly authorityLevelAllStore = AuthorityLevelEnum.ALL_STORE;
  public roleSearchTerm = '';
  /**
   * Edit mode reconciles the patched Department against the master list, so it must not run
   * before GET departments answers -- otherwise an existing Department reads as deleted.
   */
  protected departmentsLoaded$ = new BehaviorSubject<boolean>(false);
  private lastAuthorityScopesAfterLoaded: () => void;

  assetPath = environment.assetPath;

  constructor(
    protected readonly store: Store<AppStates>,
    protected readonly translate: TranslateService,
    protected readonly fb: UntypedFormBuilder,
    protected readonly logger: NGXLogger,
    protected readonly modalService: BsModalService,
    protected storeRequestService: StoreRequestService,
    protected storeAuthorityService: StoreAuthorityService,
    protected masterService: MasterService,
    protected roleService: RoleService,
    protected authGuardService: AuthGuardService,
    protected merchantRequestService: MerchantRequestService
  ) {
    super(store, modalService, false);
    this.accessStoreAuthority = [
      ClientIdTypeEnum.STORE_PORTAL,
      ClientIdTypeEnum.POS_APPL,
      ClientIdTypeEnum.TD_HAND_APPL,
      ClientIdTypeEnum.CJX_POS_APPL,
      ClientIdTypeEnum.CJX_HAND_APPL,
      ClientIdTypeEnum.CJX_POS_MANAGER_APPL,
      ClientIdTypeEnum.CJX_BUDDY_APPL,
      ClientIdTypeEnum.CJX_EYES_APPL
    ];
    this.hasStoreAuthority = false;
    this.roleList = [];
    this.roleOptions = [];
    this.departmentList = [];
    this.departmentOptions = [];
    this.departmentOptionsDisplay = [];
    this.authorityLevelOptions = [
      { value: AuthorityLevelEnum.ALL_STORE, label: 'All store' },
      { value: AuthorityLevelEnum.REGIONAL_OFFICE, label: 'Regional Office' },
      { value: AuthorityLevelEnum.REGIONAL_MANAGER, label: 'Regional Manager' },
      { value: AuthorityLevelEnum.AREA_MANAGER, label: 'Area Manager' },
      { value: AuthorityLevelEnum.STORE, label: 'Store' }
    ];
    this.authorityScopeOptions = [];
    this.storePanelStores = [];
    this.viewScopeDetails = [];
    this.viewStoresForStoreLevel = [];
    this.localStore = this.store.pipe(untilComponentDestroyed(this));
    this.hasViewPermission = this.authGuardService.checkPermission(['user_bo_v']);
    this.hasManagePermission = this.authGuardService.checkPermission(['user_bo_m']);
  }

  ngOnInit() {
    this.initControl();
    this.initData();
    this.initState();
  }

  initControl() {
    const initialNullRequired = [{ value: null, disabled: false }, Validators.required];
    const initialNull = [{ value: null, disabled: false }];
    this.userForm = this.fb.group({
      firstName: initialNullRequired,
      lastName: initialNullRequired,
      localFirstName: initialNullRequired,
      localLastName: initialNullRequired,
      countryCode: initialNull,
      mobileNo: initialNullRequired,
      email: [{ value: null, disabled: false }, [Validators.required, emailValidator()]],
      noEmail: [{ value: false, disabled: false }],
      merchant: [
        { value: null, disabled: false },
        {
          validators: [Validators.required],
          updateOn: 'blur'
        }
      ],
      employeeCode: initialNullRequired,
      department: [{ value: null, disabled: false }, Validators.required],
      roles: [{ value: [], disabled: true }, Validators.required],
      authorityLevel: initialNull,
      authorityScope: [{ value: [], disabled: true }]
    });
  }

  initData() {
    this.store.dispatch(new RoleAllRequestAction());
    this.store.dispatch(new UserByUserNoRequestAction(this.data.userNo));

    this.loadDepartments();
    this.localStore
      .pipe(select(selectUsersListCriteria))
      .subscribe(criteriaObject => (this.criteriaObject = criteriaObject));

    this.localStore
      .pipe(
        select(selectResetPasswordResult),
        filter(result => Boolean(result) && Boolean(result.statusCode)),
        tap(result => this.resetPasswordEvent(result.statusCode, ResetPasswordTypeEnum.PASSWORD))
      )
      .subscribe();

    this.localStore
      .pipe(
        select(selectChangeStatusUsersByUsernameResult),
        filter(result => Boolean(result) && Boolean(result.statusCode)),
        tap(result => {
          if (result.statusCode === '200') {
            this.store.dispatch(new UserByUserNoRequestAction(this.data.userNo));
          }
          this.alertChangeStatusUser(result.statusCode);
        })
      )
      .subscribe();

    this.localStore
      .pipe(
        select(selectResetPinResult),
        filter(result => Boolean(result) && Boolean(result.statusCode)),
        tap(result => this.resetPasswordEvent(result.statusCode, ResetPasswordTypeEnum.PIN))
      )
      .subscribe();

    this.userSelectedSubscription = combineLatest([
      this.store.select(selectRoleAllList),
      this.store.select(selectUsersByNo),
      this.departmentsLoaded$.pipe(filter(Boolean))
    ]).subscribe(([roles, userResponse]) => {
      if (roles && userResponse) {
        this.userView = userResponse;
        this.status = userResponse.active ? UserStatusEnum.ACTIVE : UserStatusEnum.INACTIVE;
        this.setInitialValue(userResponse, roles);

        if (this.data.mode === UserPageModes.EDIT) {
          this.toggleToEditMode();
        } else if (this.isViewMode) {
          this.loadViewAuthorityScopes(userResponse.no);
        }
      }
    });
  }

  /**
   * Fail-closed (RFC-113 §8): a failed request must not look like a user without store scopes,
   * so it keeps the card visible in an error state instead of feeding an empty response through.
   */
  loadViewAuthorityScopes(userNo: string) {
    this.viewAuthorityError = false;

    this.storeAuthorityService
      .getUserAuthorityScopes(userNo)
      .pipe(
        catchError(err => {
          this.logger.error('[StoreAuthority] load user authority scopes failed', userNo, err);
          this.viewAuthorityError = true;
          return EMPTY;
        })
      )
      .subscribe(response => this.setViewAuthorityData(response));
  }

  setViewAuthorityData(response: AuthorityScopeResponse) {
    const group = (response.regionalStructures || [])[0];

    this.viewAuthorityLevel = group ? VIEW_AUTHORITY_LEVEL_MAP[group.level] || null : null;
    this.viewScopeDetails = [];
    this.viewStoresForStoreLevel = [];

    if (!group || this.viewAuthorityLevel === AuthorityLevelEnum.ALL_STORE) {
      return;
    }

    if (this.viewAuthorityLevel === AuthorityLevelEnum.STORE) {
      this.viewStoresForStoreLevel = (response.stores || []).map(store => ({
        code: store.code || '-',
        name: store.name || 'This store is no longer available.'
      }));
      return;
    }

    this.viewScopeDetails = (group.structures || [])
      .map(structure => ({
        code: structure.code,
        name: structure.name || '-',
        totalStore: structure.totalStore,
        stores: (response.stores || [])
          .filter(store => (store.regionalStructures || []).includes(structure.code))
          .map(store => ({ code: store.code, name: store.name }))
          .sort((a, b) => compareTextAsc(a.code, b.code))
      }))
      .sort((a, b) => compareTextAsc(a.code, b.code));
  }

  loadDepartments() {
    const departmentQuery = new GraphqlQueryObject();
    departmentQuery.name = MasterDataEnum.DEPARTMENTS;
    departmentQuery.fields = ['code', 'id', 'nameEn', 'nameTh'];
    departmentQuery.active = true;
    departmentQuery.sort = {
      orderBy: OrderByEnum.NAME_EN,
      orderDirection: OrderDirectionEnum.ASC
    } as GraphqlQuerySortOptions;

    this.masterService.getMasterDataByNames([departmentQuery]).subscribe({
      next: result => {
        if (result.data) {
          this.departmentList = result.data.departments;
          this.departmentOptions = this.departmentList
            .map(department => ({ ...department, displayName: this.departmentName(department) }))
            .sort((a, b) => compareTextAsc(a.displayName, b.displayName));
          this.updateDepartmentOptions();
        }

        this.departmentsLoaded$.next(true);
      },
      error: () => this.departmentsLoaded$.next(true)
    });
  }

  departmentName(department: Department): string {
    return (this.translate.currentLang === 'th' ? department.nameTh : department.nameEn) || department.nameTh;
  }

  initState() {
    this.localStore.pipe(select(selectUserCreateResponseError)).subscribe(error => {
      if (error && error.code) {
        switch (error.code) {
          case '02003':
            const message = this.translate.instant('ERROR_CODE.' + error.code);
            this.showAlert('Failed', message);
            break;
          case '02008':
            const PhoneNo = this.userForm.get('mobileNo').value;
            this.duplicatePhoneNo = PhoneNo.number;
            this.userForm.get('mobileNo').setValidators(this.isMobileNoDuplicatedValidator);
            this.userForm.get('mobileNo').updateValueAndValidity();
            break;
          case '02010':
            this.userForm.get('email').setValidators(Validators.email);
            this.userForm.get('email').updateValueAndValidity();
            break;
          default:
            this.showAlert('Failed', error.message);
            break;
        }
      }
    });
  }

  onChangeNoEmail(checked: boolean) {
    const email = this.userForm.controls['email'];

    if (checked) {
      email.setValue(null);
      email.clearValidators();
      email.disable();
    } else {
      email.enable();
      email.setValidators([Validators.required, emailValidator()]);
    }

    email.updateValueAndValidity();
  }

  updateDepartmentOptions() {
    this.departmentOptionsDisplay = this.departmentOptions;
  }

  onChangeDepartment() {
    const roles = this.userForm.controls['roles'];

    roles.setValue([]);
    this.updateRoleOptions();
    if (this.selectedDepartment) {
      roles.enable();
    } else {
      roles.disable();
    }

    this.onChangeRole();
  }

  onChangeRole() {
    const selectedRoles: RoleViewResponse[] = this.userForm.controls['roles'].value || [];

    this.hasStoreAuthority = selectedRoles.some(role =>
      (role.applications || []).some(app => this.accessStoreAuthority.includes(ClientIdTypeEnum[app]))
    );

    const authorityLevel = this.userForm.controls['authorityLevel'];
    if (this.hasStoreAuthority) {
      authorityLevel.setValidators(Validators.required);
      authorityLevel.updateValueAndValidity();
      this.loadAuthorityScopes();
    } else {
      this.clearStoreAuthority();
    }
  }

  clearStoreAuthority() {
    const authorityLevel = this.userForm.controls['authorityLevel'];
    const authorityScope = this.userForm.controls['authorityScope'];

    authorityLevel.setValue(null);
    authorityLevel.clearValidators();
    authorityLevel.updateValueAndValidity();

    authorityScope.setValue([]);
    authorityScope.clearValidators();
    authorityScope.disable();
    authorityScope.updateValueAndValidity();

    this.authorityScopeOptions = [];
    this.authorityScopeError = false;
    this.lastAuthorityScopesAfterLoaded = null;
    this.closeStorePanel();
  }

  onChangeAuthorityLevel() {
    const level: AuthorityLevelEnum = this.userForm.controls['authorityLevel'].value;
    const authorityScope = this.userForm.controls['authorityScope'];

    authorityScope.setValue([]);
    this.authorityScopeOptions = [];
    this.closeStorePanel();

    if (!level || level === AuthorityLevelEnum.ALL_STORE) {
      authorityScope.clearValidators();
      authorityScope.disable();
    } else {
      authorityScope.setValidators(Validators.required);
      authorityScope.enable();
      this.loadAuthorityScopes(() => (this.authorityScopeOptions = this.buildScopeOptions(level)));
    }

    authorityScope.updateValueAndValidity();
  }

  /**
   * A failed request is not cached, so Retry (or the next role/level change) fetches again and
   * re-runs the last afterLoaded, which restores the saved scopes in Edit mode.
   */
  loadAuthorityScopes(afterLoaded?: () => void) {
    if (this.authorityScopeResponse) {
      if (afterLoaded) {
        afterLoaded();
      }
      return;
    }

    if (afterLoaded) {
      this.lastAuthorityScopesAfterLoaded = afterLoaded;
    }
    this.authorityScopeError = false;

    this.storeAuthorityService
      .getAuthorityScopes()
      .pipe(
        catchError(err => {
          this.logger.error('[StoreAuthority] load authority scopes failed', err);
          this.authorityScopeError = true;
          return EMPTY;
        })
      )
      .subscribe(response => {
        this.authorityScopeResponse = {
          regionalStructures: (response && response.regionalStructures) || [],
          stores: (response && response.stores) || []
        };

        const callback = this.lastAuthorityScopesAfterLoaded;
        this.lastAuthorityScopesAfterLoaded = null;
        if (callback) {
          callback();
        }
      });
  }

  retryLoadAuthorityScopes() {
    this.loadAuthorityScopes(this.lastAuthorityScopesAfterLoaded);
  }

  buildScopeOptions(level: AuthorityLevelEnum): AuthorityScopeOption[] {
    if (!this.authorityScopeResponse) {
      return [];
    }

    if (level === AuthorityLevelEnum.STORE) {
      return this.authorityScopeResponse.stores
        .map(store => ({
          value: store.no,
          code: store.code,
          name: store.name,
          displayName: `${store.code} - ${store.name}`
        }))
        .sort((a, b) => compareTextAsc(a.code, b.code));
    }

    const group = this.authorityScopeResponse.regionalStructures.find(item => item.level === level);

    return ((group && group.structures) || [])
      .map(structure => ({
        value: structure.code,
        code: structure.code,
        name: structure.name,
        displayName: `${structure.code} ${structure.name}`,
        totalStore: structure.totalStore
      }))
      .sort((a, b) => compareTextAsc(a.code, b.code));
  }

  storesUnderScope(scope: AuthorityScopeOption): AuthorityScopeStore[] {
    if (!this.authorityScopeResponse) {
      return [];
    }

    return this.authorityScopeResponse.stores
      .filter(store => (store.regionalStructures || []).includes(scope.code))
      .sort((a, b) => compareTextAsc(a.code, b.code));
  }

  onSearchRole(term: string, select: NgSelectComponent) {
    this.roleSearchTerm = term;
    select.filter(term);
  }

  clearRoleSearch() {
    this.roleSearchTerm = '';
  }

  openStorePanel(scope: AuthorityScopeOption, event: Event, select: NgSelectComponent) {
    event.stopPropagation();
    select.close();

    if (this.storePanelScope && this.storePanelScope.value === scope.value) {
      this.closeStorePanel();
      return;
    }

    this.storePanelScope = scope;
    this.storePanelStores = this.storesUnderScope(scope);
  }

  closeStorePanel() {
    this.storePanelScope = null;
    this.storePanelStores = [];
  }

  get selectedDepartment(): DepartmentOption {
    return this.userForm.controls['department'].value;
  }

  compareDepartment = (a: Department, b: Department): boolean => {
    return Boolean(a) && Boolean(b) && a.code === b.code;
  };

  updateRoleOptions() {
    const department = this.selectedDepartment;

    this.roleOptions = department
      ? this.roleList
          .filter(role => role.isAnyDepartment || role.departmentCode === department.code)
          .sort((a, b) => compareTextAsc(a.name, b.name))
      : [];
  }

  get isAllStoreLevel(): boolean {
    return this.userForm.controls['authorityLevel'].value === AuthorityLevelEnum.ALL_STORE;
  }

  get isStoreLevel(): boolean {
    return this.userForm.controls['authorityLevel'].value === AuthorityLevelEnum.STORE;
  }

  get authorityScopePlaceholder(): string {
    return this.isAllStoreLevel ? 'All store' : 'Please select...';
  }

  setInitialValue(userView: UserViewResponse, roles: RoleViewResponse[]) {
    if (userView === null) {
      return;
    }

    this.userForm.controls['firstName'].setValue(userView.firstName);
    this.userForm.controls['lastName'].setValue(userView.lastName);
    this.userForm.controls['localFirstName'].setValue(userView.localFirstName);
    this.userForm.controls['localLastName'].setValue(userView.localLastName);

    const mobilePhone = userView.countryCode && userView.mobileNo ? userView.countryCode + userView.mobileNo : null;

    this.userForm.controls['mobileNo'].setValue(mobilePhone);
    this.userForm.controls['countryCode'].setValue(userView.countryCode);

    this.userForm.controls['email'].setValue(userView.email);
    const noEmail = userView.isRequiredEmail === false;
    this.userForm.controls['noEmail'].setValue(noEmail);
    this.onChangeNoEmail(noEmail);
    this.userForm.controls['employeeCode'].setValue(userView.employeeCode);
    this.userForm.controls['department'].setValue(userView.department);
    this.userForm.controls['department'].setValidators([Validators.required, this.departmentValidator]);
    this.userForm.controls['merchant'].setValue({
      value: userView.merchant.merchantNo,
      merchant: userView.merchant.merchantNo,
      merchantNameDisplay: userView.merchant.merchantName
    });
    this.updateDepartmentOptions();

    if (userView.roles.some(v => this.roleStoreOwner.indexOf(v) > -1)) {
      this.roleList = roles.filter(v => this.roleStoreOwner.indexOf(v.code) > -1);
      this.isStoreOwner = true;
    } else {
      this.roleList = roles.filter(v => this.roleStoreOwner.indexOf(v.code) === -1);
    }

    const selectedRoles = (userView.roles || [])
      .map(role => this.roleList.find((roleValue: RoleViewResponse) => roleValue.code === role))
      .filter(Boolean);

    this.updateRoleOptions();
    this.userForm.controls['roles'].setValue(selectedRoles);
    if (userView.department) {
      this.userForm.controls['roles'].enable();
    }
    this.onChangeRole();
    this.setInitialStoreAuthority(userView);

    if (this.isViewMode) {
      this.userForm.disable();
    }
  }

  setInitialStoreAuthority(userView: UserViewResponse) {
    if (!this.hasStoreAuthority) {
      return;
    }

    const authorityLevel = this.userForm.controls['authorityLevel'];
    authorityLevel.setValidators(Validators.required);

    const storeAuthority = userView.storeAuthority;
    if (!storeAuthority || !storeAuthority.level) {
      authorityLevel.updateValueAndValidity();
      return;
    }

    const level = storeAuthority.level as AuthorityLevelEnum;
    authorityLevel.setValue(level);
    authorityLevel.updateValueAndValidity();

    if (level === AuthorityLevelEnum.ALL_STORE) {
      return;
    }

    const authorityScope = this.userForm.controls['authorityScope'];
    authorityScope.setValidators(Validators.required);
    authorityScope.enable();

    this.loadAuthorityScopes(() => {
      // The scopes answer lands after changeToEditMode(), which may have cleared the whole
      // Store Authority section because the Department or Role behind it was deleted.
      if (authorityLevel.value !== level) {
        return;
      }

      this.authorityScopeOptions = this.buildScopeOptions(level);

      const selectedScopes = (storeAuthority.scopes || [])
        .map(scopeValue => this.authorityScopeOptions.find(scope => scope.value === scopeValue))
        .filter(Boolean);

      authorityScope.setValue(selectedScopes);

      if (this.isViewMode) {
        authorityScope.disable();
      }
    });
  }

  loadMerchant(initialTerm: string) {
    this.merchantList = concat(
      of([]),
      this.merchantSearchInput$.pipe(
        startWith(initialTerm),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.merchantSearchLoading = true)),
        switchMap(term =>
          this.storeRequestService.searchMerchantByName({ searchCriteria: term }, 20).pipe(
            catchError(() => of([])),
            tap(() => {
              this.merchantSearchLoading = false;
            })
          )
        )
      )
    );
  }

  onExit() {
    this.isFormDirty = this.userForm.touched || this.isFormDirty;
    if (this.isFormDirty) {
      const initialState: ConfirmModal = {
        title: this.translate.instant('LEAVE_WITHOUT_SAVING'),
        okText: this.translate.instant('STAY_ON_PAGE'),
        cancelText: this.translate.instant('LEAVE'),
        message: this.translate.instant('CONFIRM_LEAVE_WITHOUT_SAVING')
      };

      this.notifyParent.emit({
        initialState,
        notificationType: NotificationTypeEnum.CONFIRM
      });
    } else {
      this.notifyParent.emit({ notificationType: NotificationTypeEnum.CANCEL, result: null });
    }
  }

  onSubmit() {
    this.submitted = true;

    if (this.userForm.invalid) {
      return;
    }

    const data = this.prepareData();
    this.sendToSubmit(data);
  }

  sendToSubmit(data: UserDto) {
    const confirmModalRef = this.modalService.show(ConfirmModalComponent, {
      initialState: {
        title: 'Confirm',
        message: 'Are you sure you want to submit?'
      }
    });

    confirmModalRef.content.action
      .pipe(untilComponentDestroyed(this))
      .subscribe((result: ModalButtonResponseEnum) => {
        if (result === ModalButtonResponseEnum.OK) {
          this.isSendToSubmit = true;
          this.store.dispatch(new UserUpdateRequestAction(data));
        }
      });
  }

  prepareData(): UserDto {
    const rawData = this.userForm.getRawValue();
    const dataRequest = {
      ...this.userView,
      ...rawData,
      ...this.prepareUserRequest(rawData)
    } as UserDto;

    this.removeFormOnlyFields(dataRequest);
    return dataRequest;
  }

  prepareUserRequest(rawData: any) {
    return {
      birthDate: null,
      gender: null,
      countryCode: rawData.mobileNo && rawData.mobileNo.internationalNumber.split(' ')[0],
      mobileNo: rawData.mobileNo && rawData.mobileNo.nationalNumber.replace(/ /g, ''),
      isRequiredEmail: !rawData.noEmail,
      email: rawData.noEmail ? null : rawData.email,
      department: this.prepareDepartment(rawData.department),
      roles: (rawData.roles || []).map((role: RoleViewResponse) => role.code),
      accessAllStores: null,
      accessStoreGroups: null,
      accessStores: null,
      storeAuthority: this.prepareStoreAuthority(rawData)
    };
  }

  removeFormOnlyFields(dataRequest: UserDto) {
    delete dataRequest['merchant'];
    delete dataRequest['noEmail'];
    delete dataRequest['authorityLevel'];
    delete dataRequest['authorityScope'];
  }

  prepareDepartment(department: DepartmentOption): Department {
    if (!department) {
      return null;
    }

    const { displayName, ...departmentRequest } = department;
    return departmentRequest as Department;
  }

  prepareStoreAuthority(rawData: any): StoreAuthority {
    const level: AuthorityLevelEnum = rawData.authorityLevel;

    if (!this.hasStoreAuthority || !level) {
      return null;
    }

    return {
      level,
      scopes:
        level === AuthorityLevelEnum.ALL_STORE
          ? []
          : (rawData.authorityScope || []).map((scope: AuthorityScopeOption) => scope.value)
    };
  }

  showHistory() {
    const initialState = {
      title: 'History',
      action: HistoryType.REQUEST,
      historyHeader: `User ID: ${this.userView.no}`,
      historyType: HistoryType.USER,
      auditLogs: this.userView.auditLogs
    };
    this.modalService.show(HistoryComponent, {
      initialState
    });
  }

  toggleToEditMode() {
    const isRoleStoreOwner = this.userView.roles?.find(role => {
      return this.roleStoreOwner.includes(role);
    });

    if (isRoleStoreOwner) {
      this.merchantRequestService.getMerchantPending(this.userView.merchant.merchantNo).subscribe({
        next: () => {
          this.changeToEditMode();
        },
        error: _ => {
          const initialState = {
            title: 'Failed',
            message:
              'Not allow to edit this user.<br/> Merchant Request associated with this user has been edited and waiting for approval.'
          };
          const modalRef = this.modalService.show(AlertModalComponent, { initialState });
          modalRef.content.action
            .pipe(
              filter(result => result === ModalButtonResponseEnum.OK),
              take(1)
            )
            .subscribe({
              next: () => {
                modalRef.hide();
              }
            });
        }
      });
    } else {
      this.changeToEditMode();
    }
  }

  changeToEditMode() {
    this.data.mode = UserPageModes.EDIT;
    this.data.title = 'Edit User';

    this.userForm.enable();
    this.userForm.controls['merchant'].disable();

    if (this.isStoreOwner) {
      this.userForm.controls['roles'].disable();
    }

    if (this.userForm.controls['noEmail'].value) {
      this.userForm.controls['email'].disable();
    }

    this.reconcileDeletedDepartment();

    if (!this.hasStoreAuthority || !this.userForm.controls['authorityLevel'].value || this.isAllStoreLevel) {
      this.userForm.controls['authorityScope'].disable();
    }
  }

  /**
   * A Department deleted from the master data leaves both Department and Role blank, matching the
   * deleted-data AC. Roles and Authority Scope drop their deleted entries while patching, so only
   * Department needs reconciling here.
   */
  reconcileDeletedDepartment() {
    if (!this.userView.department) {
      return;
    }

    const department = this.departmentOptionsDisplay.find(option => option.code === this.userView.department.code);

    this.userForm.controls['department'].setValue(department || null);

    if (!department) {
      this.onChangeDepartment();
    }
  }

  showAlert(title: string, message: string) {
    const initialState = {
      title,
      message
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  roleDisplayName(code: string): string {
    const role = this.roleList.find(r => r.code === code);
    return role ? role.name : code;
  }

  get authorityLevelLabel(): string {
    const option = this.authorityLevelOptions.find(o => o.value === this.viewAuthorityLevel);
    return option ? option.label : '';
  }

  get mobilePhoneDisplay(): string {
    if (!this.userView || !this.userView.mobileNo) {
      return '-';
    }
    return this.userView.mobileNo;
  }

  get departmentDisplay(): string {
    if (!this.userView || !this.userView.department) {
      return '-';
    }
    return this.departmentName(this.userView.department);
  }

  get isViewMode(): boolean {
    return [UserPageModes.VIEW].includes(this.data.mode);
  }

  get isCreateMode(): boolean {
    return [UserPageModes.CREATE].includes(this.data.mode);
  }

  get isEditMode(): boolean {
    return [UserPageModes.EDIT].includes(this.data.mode);
  }

  isPhoneError(control: AbstractControl): string {
    return this.submitted && control.errors ? 'is-invalid' : '';
  }

  get isMobileNoDuplicatedValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value !== null) {
        return control.value.number === this.duplicatePhoneNo ? { isDuplicatedMobile: true } : null;
      }
      return null;
    };
  }

  get departmentValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      const isValidDepartment = this.departmentList.some(
        d => d.code === control.value.code && d.nameTh === control.value.nameTh
      );
      return !isValidDepartment ? { isInvalidDepartment: true } : null;
    };
  }

  handleResetPassword() {
    if (this.userView === null) {
      return;
    }

    const initialState = {
      title: this.translate.instant('USER.RESET_PASSWORD'),
      message: this.translate.instant('USER.ARE_YOU_SURE_YOU_WANT_TO_RESET_PASSWORD', {
        fullName: `${this.userView.firstName} ${this.userView.lastName}`
      }),
      okText: this.translate.instant('OK')
    };

    const confirmModalRef = this.modalService.show(ConfirmModalComponent, {
      initialState
    });

    confirmModalRef.content.action
      .pipe(untilComponentDestroyed(this))
      .subscribe((result: ModalButtonResponseEnum) => {
        if (result === ModalButtonResponseEnum.OK) {
          this.store.dispatch(new UserResetPasswordRequestAction(this.userView.userName));
        }
      });
  }

  handleChangeStatusUser() {
    if (!this.userView) {
      return;
    }
    const initialState = {
      title: this.translate.instant('USER.CONFIRM'),
      message: this.translate.instant('USER.ARE_YOU_SURE_YOU_WANT_TO_ACTIVATE', {
        activate: this.userView.active ? `deactivate` : `activate`
      }),
      okText: this.translate.instant('Submit')
    };

    const confirmModalRef = this.modalService.show(ConfirmModalComponent, {
      initialState
    });

    confirmModalRef.content.action
      .pipe(untilComponentDestroyed(this))
      .subscribe((result: ModalButtonResponseEnum) => {
        if (result === ModalButtonResponseEnum.OK) {
          const dataRequest = {
            userName: this.userView.userName,
            status: !this.userView.active
          } as ChangeStatusUserRequest;

          this.store.dispatch(new ActivateUserRequestAction(dataRequest));
        }
      });
  }

  private resetPasswordEvent(statusCode: string, type: ResetPasswordTypeEnum) {
    if (statusCode === '200') {
      this.alertSuccessResetPasswordPin(type);
    } else {
      this.alertFailedResetPasswordPin(type);
    }
  }

  alertFailedResetPasswordPin(type: ResetPasswordTypeEnum) {
    const initialState = {
      title: this.translate.instant('USER.FAILED'),
      message:
        type === ResetPasswordTypeEnum.PASSWORD
          ? this.translate.instant('USER.PASSWORD_RESET_FAILED')
          : this.translate.instant('USER.PIN_RESET_FAILED')
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  alertSuccessResetPasswordPin(type: ResetPasswordTypeEnum) {
    const initialState = {
      title: this.translate.instant('USER.SUCCESS'),
      message:
        type === ResetPasswordTypeEnum.PASSWORD
          ? this.translate.instant('USER.PASSWORD_RESET_SUCCESS', { mobileNo: this.userView.mobileNo })
          : this.translate.instant('USER.PIN_RESET_SUCCESS', { mobileNo: this.userView.mobileNo })
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  alertChangeStatusUser(statusCode: string) {
    let title: string;
    let message: string;

    if (statusCode === '200') {
      title = this.translate.instant('USER.SUCCESS');
      message = this.translate.instant('USER.CHANGE_STATUS_SUCCESS', {
        status: this.userView.active ? 'deactivated' : 'activated'
      });
    } else {
      title = this.translate.instant('USER.FAILED');
      message = this.translate.instant('USER.CHANGE_STATUS_FAILED', {
        status: this.userView.active ? 'Deactivated' : 'Activated'
      });
    }

    const initialState = {
      title,
      message
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  doAfterVersionAlertModal() {
    this.notifyParent.emit({ notificationType: NotificationTypeEnum.FORCE_CLOSE });
  }

  doAfterSuccessModal() {
    this.doAfterVersionAlertModal();
  }

  ngOnDestroy(): void {
    if (this.data.mode === UserPageModes.CREATE && this.isSendToSubmit) {
      this.criteriaObject = {
        ...this.criteriaObject,
        page: 0
      };
    }

    if (this.notifyParent) {
      this.notifyParent.unsubscribe();
    }

    if (this.userSelectedSubscription) {
      this.userSelectedSubscription.unsubscribe();
    }

    this.store.dispatch(new UserListRequestAction(this.criteriaObject));
    this.store.dispatch(new UserByUserNoResetAction());
    this.store.dispatch(new UserUpdateResponseResetAction());
    this.store.dispatch(new RoleAllResetAction());
    this.store.dispatch(new UserResetPasswordResponseResetAction());
    this.store.dispatch(new ActivateUserResetAction());
    this.store.dispatch(new UserCreateResponseResetAction());

    super.ngOnDestroy();
  }
}
